import React, { memo, useMemo, useCallback, useState, useRef } from 'react';
import { Modal, Loader, Tabs, createUseStyles, Text, BadgeNotification } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';
import { useWeb5Context } from 'lib/Web5Context';
import {
    getChartDynamicConfig,
    getTableDynamicConfig,
    getKpiDynamicConfig,
    getMapDynamicConfig,
} from 'lib/services/forceBI';
import { operators, dateFieldOperators } from 'components/Filters/operators';
import { infoToast } from 'utils/toast';
import Charts from '../Charts';

import CrudFilters from './components/CrudFilters';
import CrudFields from './components/CrudFields';
import StepsInfo from './components/StepsInfo';
import { getCrudSchema } from './config';
import { NOT_CHART_TYPES } from '../../constants';
import Empty from './components/Empty';
import useForceBIFilters from '../../hooks/useForceBIFilters';

import styles from './styles';

const useStyles = createUseStyles(styles, 'Crud');

const TABS = {
    chart: 'chart',
    filters: 'filters',
};

const Crud = memo(
    ({
        isOpen,
        onClose,
        onAfterClose,
        onConfirm,
        onChange,
        data,
        filtersComponent,
        filtersAddButtonSchema,
        forceBIUtils,
        constants,
        crossFilterIdSeparator,
        hiddenFilterIds = [],
    }) => {
        const classes = useStyles();
        const { t, getWeb4EntityFromString, entities } = useWeb5Context();
        const [tab, setTab] = useState(TABS.chart);
        const [isLoading, setIsLoading] = useState(false);
        const [isLoaded, setIsLoaded] = useState(false);
        const [config, setConfig] = useState(null);
        const [isError, setIsError] = useState(false);
        const [wrongFiltersMessage, setWrongFiltersMessage] = useState('');
        const [schema, setSchema] = useState([]);
        const [errors, setErrors] = useState([]);
        const [isConfirmLoading, setIsConfirmLoading] = useState(false);
        const [hasChanges, setHasChanges] = useState(false);
        const [isDiscardOpen, setIsDiscardOpen] = useState(false);
        const debounceTimeoutRef = useRef(null);

        const getWrongFiltersMessage = useCallback(
            (error) => {
                const { getFiltersErrorMessage } = forceBIUtils;
                const errorObj = error.response?.data || null;
                if (errorObj?.error && errorObj?.fields && Object.keys(errorObj?.fields).length) {
                    const message = getFiltersErrorMessage({
                        message: errorObj.error,
                        entityFields: errorObj.fields,
                    });
                    setWrongFiltersMessage(message);
                }
            },
            [forceBIUtils],
        );

        const getIsMap = useCallback((data) => {
            if (data?.chartType === NOT_CHART_TYPES.map) return true;
            return false;
        }, []);

        const getIsKpi = useCallback((data) => {
            if (data?.chartType === NOT_CHART_TYPES.kpi) return true;
            return false;
        }, []);

        const getIsTable = useCallback((data) => {
            if (data?.chartType === NOT_CHART_TYPES.table) return true;
            return false;
        }, []);

        const completeLoad = useCallback(() => {
            setIsLoading(false);
            setIsLoaded(true);
        }, []);

        const updateVisuals = useCallback(
            ({ data }) => {
                setIsError(false);
                setWrongFiltersMessage('');
                setIsLoading(true);
                setIsLoaded(false);
                const { processDataToSendFromCrud } = forceBIUtils;

                const newData = processDataToSendFromCrud({ data });

                let promise;

                if (getIsMap(newData)) promise = getMapDynamicConfig(newData);
                else if (getIsKpi(newData)) promise = getKpiDynamicConfig(newData);
                else if (getIsTable(newData)) promise = getTableDynamicConfig(newData);
                else promise = getChartDynamicConfig(newData);

                if (!promise) return;

                promise
                    .then((response) => {
                        if (!response?.data) {
                            setIsError(false);
                            setConfig(null);
                            return;
                        }
                        setConfig(response.data);
                    })
                    .catch((error) => {
                        getWrongFiltersMessage(error);
                        setConfig(null);
                        setIsError(true);
                    })
                    .finally(() => completeLoad());
            },
            [getIsTable, getIsKpi, getIsMap, forceBIUtils, completeLoad, getWrongFiltersMessage],
        );

        const getCanRenderChart = useCallback((data) => {
            const hasXAxisDateField = data?.xAxis?.isDateField || false;
            const hasXAxisAggregationDate = data?.xAxisAggregationDate?.value;
            const hasGeneralData =
                data?.chartType &&
                data?.entity?.value &&
                data?.yAxis?.value &&
                data?.xAxis?.value &&
                data?.yAxisAggregation?.value;
            if (hasXAxisDateField) {
                if (hasGeneralData && hasXAxisAggregationDate) return true;
                return false;
            } else return hasGeneralData;
        }, []);

        const afterOnChangeFilter = useCallback(
            ({ filters, crossFilters, operators, crossOperators }) => {
                if (!hasChanges) setHasChanges(true);
                if (debounceTimeoutRef.current) {
                    clearTimeout(debounceTimeoutRef.current);
                }

                const newData = {
                    ...(data || {}),
                    filters,
                    crossFilters,
                    operators,
                    crossOperators,
                };

                if (getCanRenderChart(newData)) {
                    debounceTimeoutRef.current = setTimeout(() => {
                        updateVisuals({ data: newData });
                    }, 300);
                }
            },
            [data, getCanRenderChart, updateVisuals, hasChanges],
        );

        const forceBIFiltersProps = useForceBIFilters({
            afterOnChange: afterOnChangeFilter,
            constants,
            hiddenFilterIds,
        });

        const numberOfFilters = useMemo(() => {
            const { getUniqueFilters } = forceBIUtils;
            const { filters, crossFilters, operators, crossOperators } = forceBIFiltersProps;

            const { uiFilterNames } = getUniqueFilters({
                filters,
                crossFilters,
                operators,
                crossOperators,
            });
            return uiFilterNames.length || 0;
        }, [forceBIUtils, forceBIFiltersProps]);

        const tabs = useMemo(() => {
            return [
                {
                    key: TABS.chart,
                    title: t('label_forcebi_crud_tab_chart'),
                },
                {
                    key: TABS.filters,
                    title: (
                        <div className={classes.filtersTab}>
                            <Text>{t('label_forcebi_crud_tab_filters')}</Text>
                            {numberOfFilters > 0 && (
                                <BadgeNotification size="small">
                                    {numberOfFilters}
                                </BadgeNotification>
                            )}
                        </div>
                    ),
                },
            ];
        }, [numberOfFilters, t, classes]);

        // This filter exists to set a default filter depending on the entity and limitate the queries
        const createDynamicDefaultFilter = useCallback(
            (value) => {
                const { hasActiveFilters, customChangeOperator, customSaveCache } =
                    forceBIFiltersProps;

                if (!hasActiveFilters) {
                    const { CONTACTS, COMPANIES, OPPORTUNITIES, SALESORDERS, ACTIVITIES } =
                        entities;
                    const entity = getWeb4EntityFromString(value.value);
                    let crossEntity = null;

                    const reverseDateFieldOperators = Object.entries(
                        dateFieldOperators.operators,
                    ).reduce((obj, [key, value]) => {
                        obj[value] = key;
                        return obj;
                    }, {});

                    const { THIS_YEAR } = operators;

                    const fieldIdByEntity = {
                        [CONTACTS.entity]: 'fcreado',
                        [COMPANIES.entity]: 'fcreado',
                        [OPPORTUNITIES.entity]: 'fcreado',
                        [SALESORDERS.entity]: 'fcreado',
                        [ACTIVITIES.entity]: 'date',
                    };

                    let fieldId = fieldIdByEntity[entity.entity];

                    if (!fieldId) return;

                    const operatorObj = {
                        [fieldId]: {
                            label: t(reverseDateFieldOperators[THIS_YEAR]),
                            value: THIS_YEAR,
                        },
                    };

                    const cache = {
                        visibleFilters: [],
                        openFilters: {},
                    };

                    if (entity.entity !== COMPANIES.entity) {
                        crossEntity = entity.entity;
                        const crossFilter = `${entity.entity}${crossFilterIdSeparator}${fieldId}`;
                        if (!cache.visibleFilters.includes(crossFilter)) {
                            cache.visibleFilters.push(crossFilter);
                        }
                        cache.openFilters[crossFilter] = true;
                    } else {
                        if (!cache.visibleFilters.includes(fieldId)) {
                            cache.visibleFilters.push(fieldId);
                        }
                        cache.openFilters[fieldId] = true;
                    }

                    customSaveCache(cache); // Make filter visible
                    customChangeOperator({ operators: operatorObj, crossEntity });

                    let entityName = t(entity.labels.singular);

                    entityName =
                        entityName.charAt(0).toUpperCase() + entityName.slice(1).toLowerCase();

                    infoToast({
                        t,
                        title: t('label_new_filter_added'),
                        text: t('label_new_creation_date_filter_added_description', [entityName]),
                    });
                }
            },
            [getWeb4EntityFromString, entities, crossFilterIdSeparator, t, forceBIFiltersProps],
        );

        const handleOnChange = useCallback(
            ({ fieldId, value }) => {
                let newData = { ...(data || {}) };
                if (!value && newData?.fieldId) {
                    delete newData?.fieldId;
                } else {
                    newData[fieldId] = value;
                }

                if (fieldId === 'entity') {
                    createDynamicDefaultFilter(value);

                    newData.yAxis = null;
                    newData.xAxis = null;
                    newData.xAxisEntity = null;
                    newData.xAxisBreakdown = null;
                    newData.xAxisBreakdownEntity = null;
                    newData.xAxisBreakdownAggregationDate = null;
                    if (newData?.xAxisAggregationDate) {
                        delete newData?.xAxisAggregationDate;
                    }
                } else if (fieldId === 'xAxisEntity') {
                    newData.xAxis = null;
                } else if (fieldId === 'xAxisBreakdownEntity') {
                    newData.xAxisBreakdown = null;
                } else if (fieldId === 'yAxis') {
                    // Those yAxis options containing 'id' after '.' should be considered as numeric
                    // and only have one yAxisAggregation method which is 'count'.
                    const isNumeric = value.value.split('.')[1] === 'id';
                    if (isNumeric && newData?.yAxisAggregation?.value !== 'count') {
                        newData.yAxisAggregation = null;
                    }
                } else if (fieldId === 'xAxis' && !value?.isDateField) {
                    if (newData?.xAxisAggregationDate) delete newData.xAxisAggregationDate;
                } else if (fieldId === 'xAxisBreakdown' && !value?.isDateField) {
                    if (newData?.xAxisBreakdownAggregationDate)
                        delete newData.xAxisBreakdownAggregationDate;
                }
                if (getCanRenderChart(newData) && fieldId !== 'title') {
                    const { filters, crossFilters, operators, crossOperators } =
                        forceBIFiltersProps;
                    updateVisuals({
                        data: { ...newData, filters, crossFilters, operators, crossOperators },
                    });
                }

                if (data && Object.keys(data).length > 0 && !hasChanges) setHasChanges(true);
                onChange && onChange(newData);
            },
            [
                data,
                onChange,
                getCanRenderChart,
                updateVisuals,
                forceBIFiltersProps,
                hasChanges,
                createDynamicDefaultFilter,
            ],
        );

        const handleOnClose = useCallback(() => {
            setHasChanges(false);
            setIsDiscardOpen(false);
            onClose && onClose();
        }, [onClose]);

        const handleOnAfterClose = useCallback(() => {
            const { reset } = forceBIFiltersProps;
            reset();
            setConfig(null);
            onAfterClose && onAfterClose();
        }, [onAfterClose, forceBIFiltersProps]);

        const onAfterOpen = useCallback(() => {
            setTab(TABS.chart);
            setIsLoaded(false);
            setIsError(false);
            setErrors([]);
            const crudSchema = getCrudSchema({ t });
            setSchema(crudSchema);

            const { init, reset } = forceBIFiltersProps;
            const { processFiltersToInitCrud } = forceBIUtils;

            if (data?.id) {
                setIsLoading(true);

                const initProps = processFiltersToInitCrud({
                    filters: data.filters,
                    crossFilters: data.crossFilters,
                    operators: data.operators,
                    crossOperators: data.crossOperators,
                });
                init({ ...initProps });

                updateVisuals({ data });
            } else {
                reset();
            }
        }, [data, forceBIFiltersProps, forceBIUtils, t, updateVisuals]);

        const getMandatoryFields = useCallback(() => {
            let mandatory = [];

            schema.forEach((current) => {
                if (current?.fields?.length) {
                    current.fields.forEach((field) => {
                        if (field.subFields?.length) {
                            field.subFields.forEach((subField) => {
                                if (subField.isRequired) {
                                    mandatory.push(subField.id);
                                }
                            });
                        } else if (
                            field.getIsRequired &&
                            field.getIsRequired({ fieldId: field.id, data })
                        ) {
                            mandatory.push(field.id);
                        } else if (field.isRequired) mandatory.push(field.id);
                    });
                }
            });

            return mandatory;
        }, [schema, data]);

        const getErrors = useCallback(() => {
            const newErrors = getMandatoryFields().reduce((arr, current) => {
                if (!data?.[current]) arr.push(current);
                return arr;
            }, []);
            setErrors(newErrors);
            return newErrors;
        }, [data, getMandatoryFields]);

        const onOpenDiscardModal = useCallback(() => {
            if (hasChanges) return setIsDiscardOpen(true);
            handleOnClose();
        }, [hasChanges, handleOnClose]);

        const onCloseDiscardModal = useCallback(() => {
            setIsDiscardOpen(false);
        }, []);

        const handleOnConfirm = useCallback(() => {
            const newErrors = getErrors();
            if (newErrors?.length) {
                setTab(TABS.chart);
                return;
            }

            setIsConfirmLoading(true);

            const { processDataToSendFromCrud } = forceBIUtils;
            const { filters, crossFilters, operators, crossOperators } = forceBIFiltersProps;

            const newData = {
                ...data,
                filters,
                crossFilters,
                operators,
                crossOperators,
            };

            const finalData = processDataToSendFromCrud({
                data: newData,
            });

            onConfirm &&
                onConfirm({ data: finalData })
                    .then(() => {
                        setTab(TABS.chart);
                        setErrors([]);
                        setIsConfirmLoading(false);
                    })
                    .catch(() => {
                        setIsConfirmLoading(false);
                    });
        }, [onConfirm, getErrors, data, forceBIUtils, forceBIFiltersProps]);

        const shouldDisplayInfo = useMemo(() => {
            const fields = ['chartType', 'entity', 'yAxis', 'xAxis', 'yAxisAggregation'];
            if (data?.xAxis?.isDateField) fields.push('xAxisAggregationDate');
            const missingFields = fields.filter((current) => !data?.[current]);
            return missingFields?.length > 0;
        }, [data]);

        const updateAfterError = useCallback(() => {
            const { filters, crossFilters, operators, crossOperators } = forceBIFiltersProps;

            const newData = {
                ...(data || {}),
                filters,
                crossFilters,
                operators,
                crossOperators,
            };

            updateVisuals({ data: newData });
        }, [data, forceBIFiltersProps, updateVisuals]);

        const onReturnToMainTab = useCallback(() => {
            if (tab === TABS.chart) return Promise.resolve();
            return new Promise((resolve) => {
                setTab(TABS.chart);
                setTimeout(() => {
                    resolve();
                }, 150);
            });
        }, [tab]);

        return (
            <>
                <Modal
                    className={classes.root}
                    title={
                        data?.id
                            ? t('label_edit_widget')
                            : t('label_forcebi_crud_title_placeholder')
                    }
                    size="full"
                    isOpen={isOpen}
                    onRequestClose={onOpenDiscardModal}
                    onAfterClose={handleOnAfterClose}
                    onCancel={onOpenDiscardModal}
                    onConfirm={handleOnConfirm}
                    isConfirmLoading={isConfirmLoading}
                    isConfirmDisabled={!!wrongFiltersMessage || isError}
                    cancelText={t('action_forcebi_crud_cancel')}
                    confirmText={t('action_forcebi_crud_save')}
                    onAfterOpen={onAfterOpen}
                    overrides={{
                        content: { style: { height: '100%' } },
                    }}
                    shouldCloseOnOverlayClick={false}
                >
                    <div className={classes.container}>
                        <div className={classes.left}>
                            <div className={classes.leftContent}>
                                {isLoading && (
                                    <div className={classes.loader}>
                                        <Loader size="big" />
                                    </div>
                                )}
                                {!isLoading &&
                                    getCanRenderChart(data) &&
                                    !shouldDisplayInfo &&
                                    !isError && (
                                        <Charts chartType={data.chartType} config={config} />
                                    )}
                                {isLoaded && !isLoading && isError && (
                                    <>
                                        {wrongFiltersMessage && (
                                            <Empty type="error" title={wrongFiltersMessage} />
                                        )}
                                        {!wrongFiltersMessage && (
                                            <Empty type="error" onRefresh={updateAfterError} />
                                        )}
                                    </>
                                )}
                                {!isLoading &&
                                    !getCanRenderChart(data) &&
                                    shouldDisplayInfo &&
                                    !isError && (
                                        <StepsInfo
                                            data={data}
                                            onReturnToMainTab={onReturnToMainTab}
                                        />
                                    )}
                            </div>
                        </div>
                        <div className={classes.right}>
                            <div className={classes.tabsContainer}>
                                <Tabs
                                    className={classes.tabs}
                                    onChange={setTab}
                                    tabs={tabs}
                                    activeKey={tab}
                                />
                            </div>
                            <div className={classes.formContainer}>
                                <div
                                    className={classnames(classes.crudFields, {
                                        [classes.hidden]: tab !== TABS.chart,
                                    })}
                                >
                                    <CrudFields
                                        schema={schema}
                                        data={data}
                                        onChange={handleOnChange}
                                        errors={errors}
                                    />
                                </div>
                                {/*
                            We use classes to hide and show CrudFilters instead of conditional rendering to keep continuity inside
                            AddModal selections and be able to remove existing applied filters when those are unselected from
                            the AddModal.
                            */}
                                <div
                                    className={classnames(classes.crudFilters, {
                                        [classes.hidden]: tab !== TABS.filters,
                                    })}
                                >
                                    <CrudFilters
                                        entity={forceBIUtils.entity}
                                        filtersComponent={filtersComponent}
                                        filtersAddButtonSchema={filtersAddButtonSchema}
                                        forceBIFiltersProps={forceBIFiltersProps}
                                        hiddenFilterIds={hiddenFilterIds}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal>
                <Modal
                    isOpen={isDiscardOpen}
                    size="small"
                    title={t('action_discard_changes')}
                    confirmText={t('action_confirm_lose_changes')}
                    cancelText={t('action_cancel')}
                    onConfirm={handleOnClose}
                    onCancel={onCloseDiscardModal}
                    onRequestClose={onCloseDiscardModal}
                    useCornerClose={false}
                    isConfirmDisabled={isConfirmLoading}
                    isConfirmLoading={isConfirmLoading}
                >
                    <Text>{t('label_discard_unsaved_changes')}</Text>
                </Modal>
            </>
        );
    },
);

export default Crud;
